import { useState, useEffect } from 'react'
import {
  Center,
  Text,
  Box,
  FormControl,
  FormLabel,
  Select,
  Heading,
  HStack,
  IconButton,
} from '@chakra-ui/react'
import { DatePicker } from 'antd'
import moment from 'moment/moment'
import { FaFileExport } from 'react-icons/fa'
import axios from 'axios'
import { httpsCallable } from 'firebase/functions'
import { functions } from 'config/firebase'
import { useQuery } from 'react-query'
import SourceSummaryTable from 'components/SourceSummaryTable'

const createExcelReportSourceSummary = httpsCallable(
  functions,
  'excelReportSourceSummary'
)
const getSourceSummaryReport = httpsCallable(
  functions,
  'getSourceSummaryReport'
)

export function SourceSummary({ data, branches, managerBranchId }) {
  const [searchQuery, setSearch] = useState({
    branch: managerBranchId || '',
    month: moment().format('YYYY'),
    summary: 'annually',
  })
  const [dataQuery, setDataQuery] = useState({
    result: [],
    uniqueCourse: [],
  })

  const [downloadIsLoading, setDownloadIsLoading] = useState(false)

  const { data: reportSourceSummary, isLoading } = useQuery(
    [
      'sourceSummaryReport',
      searchQuery?.summary,
      searchQuery?.month,
      searchQuery?.branch,
    ],
    async () => {
      const response = await getSourceSummaryReport({
        branchId: searchQuery?.branch,
        month: searchQuery?.month,
      })

      if (!response.data) {
        throw new Error('Network response was not ok')
      }
      return response.data
    },
    {
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  )

  const columnsData = [
    {
      Header: searchQuery.summary === 'annually' ? 'เดือน' : 'สาขา',
      justifyItems: 'center',
      minWidth: '120px',
      extra: (_, record) => {
        return (
          <Box textAlign='center' width='100%'>
            {record?.columnMain
              ? record.columnMain.charAt(0).toUpperCase() +
                record.columnMain.slice(1)
              : ''}
          </Box>
        )
      },
    },
    {
      Header: 'จำนวนผู้มาประเมิน',
      minWidth: '120px',
      justifyItems: 'center',

      extra: (_, record) => {
        return (
          <Box textAlign='center' width='100%'>
            {/* {record?.month === 'total'
              ? record?.total
              : record?.month === 'average'
              ? record?.average
              : record?.assessmentDate || '0'} */}
            {record?.assessmentDate}
          </Box>
        )
      },
    },
    {
      Header: 'อายุเฉลี่ย',
      justifyItems: 'center',
      minWidth: '120px',
      extra: (_, record) => {
        if (record?.month === 'total' || record?.columnMain === 'total')
          return null
        return (
          <Box textAlign='center' width='100%'>
            {record?.averageAge || '0'} ปี
          </Box>
        )
      },
      borderRight: '2px solid #E2E8F0',
    },
    {
      Header: 'Recommended',
      justifyItems: 'center',

      columns: [
        {
          Header: 'แพทย์',
          minWidth: '120px',
          justifyItems: 'center',

          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.doctor || '0'}
            </Box>
          ),
        },
        {
          Header: 'เพื่อน/ญาติ',
          minWidth: '120px',
          justifyItems: 'center',

          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.friend || '0'}
            </Box>
          ),
        },
        {
          Header: 'โรงเรียน',
          minWidth: '120px',
          justifyItems: 'center',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.teacher || '0'}
            </Box>
          ),
          borderRight: '2px solid #E2E8F0',
        },
      ],
    },
    {
      Header: 'Online',
      justifyItems: 'center',

      columns: [
        {
          Header: 'Facebook',
          justifyItems: 'center',
          minWidth: '120px',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.facebook || '0'}
            </Box>
          ),
        },
        // {
        //   Header: 'Google',
        //   justifyItems: 'center',
        //   minWidth: '120px',
        //   extra: (_, record) => (
        //     <Box textAlign='center' width='100%'>
        //       {record?.google || '0'}
        //     </Box>
        //   ),
        // },
        {
          Header: 'Google / Website',
          justifyItems: 'center',
          minWidth: '120px',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.website || '0'}
            </Box>
          ),
        },
        {
          Header: 'Line OA',
          justifyItems: 'center',
          minWidth: '120px',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.lineOA || '0'}
            </Box>
          ),
        },
        {
          Header: 'Youtube',
          justifyItems: 'center',
          minWidth: '120px',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.youtube || '0'}
            </Box>
          ),
        },
        {
          Header: 'Tiktok',
          justifyItems: 'center',
          minWidth: '120px',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.tiktok || '0'}
            </Box>
          ),
        },
        {
          Header: 'Instagram',
          justifyItems: 'center',
          minWidth: '120px',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.instagram || '0'}
            </Box>
          ),
          borderRight: '2px solid #E2E8F0',
        },
      ],
    },
    {
      Header: 'Offline',
      justifyItems: 'center',
      borderRight: '2px solid #E2E8F0',
      columns: [
        {
          minWidth: '250px',
          Header: 'ขับรถผ่าน/ป้ายโฆษณา/Walk in',
          justifyItems: 'center',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.advertising || '0'}
            </Box>
          ),
          borderRight: '2px solid #E2E8F0',
        },
      ],
    },
    {
      Header: 'Other',
      justifyItems: 'center',
      borderRight: '2px solid #E2E8F0',
      borderTop: '2px solid #E2E8F0',
      borderBottom: '2px solid #E2E8F0',
      columns: [
        {
          Header: 'อื่นๆ',
          minWidth: '120px',
          justifyItems: 'center',
          extra: (_, record) => (
            <Box textAlign='center' width='100%'>
              {record?.other || '0'}
            </Box>
          ),
          borderTop: '2px solid #E2E8F0',
          borderRight: '2px solid #E2E8F0',
          borderBottom: '2px solid #E2E8F0',
        },
      ],
    },
    {
      Header: 'Enrollment on Evaluation Day',
      justifyItems: 'center',
      columns: [
        ...(dataQuery.uniqueCourse || []).map((item, index) => {
          if (dataQuery.uniqueCourse && index === 0) {
            return {
              Header: 'NON-ENROLL',
              justifyItems: 'center',
              minWidth: '120px',
              extra: (_, record) => (
                <Box textAlign='center' width='100%'>
                  {record?.month === 'total'
                    ? record?.[item.value]
                    : record?.month === 'average'
                    ? record?.[item.value]
                    : record?.[item.value] || '0'}
                </Box>
              ),
            }
          }
          if (
            dataQuery.uniqueCourse &&
            index === dataQuery.uniqueCourse.length - 1
          ) {
            return {
              Header: 'MEMBER APPLY',
              justifyItems: 'center',
              minWidth: '120px',
              extra: (_, record) => (
                <Box textAlign='center' width='100%'>
                  {record?.month === 'total'
                    ? record?.[item.value]
                    : record?.month === 'average'
                    ? record?.[item.value]
                    : record?.[item.value] || '0'}
                </Box>
              ),
            }
          }
          return {
            Header: item.label,
            justifyItems: 'center',
            minWidth: '120px',
            extra: (_, record) => (
              <Box textAlign='center' width='100%'>
                {record?.month === 'total'
                  ? record?.[item.value]
                  : record?.month === 'average'
                  ? record?.[item.value]
                  : record?.[item.value] || '0'}
              </Box>
            ),
          }
        }),
      ],
    },
  ]

  async function handleDownloadFile({ data, uniqueCourse, summary }) {
    setDownloadIsLoading(true)

    const dataDownload = data.map((d) => {
      if (d?.columnMain === 'total') {
        return {
          ...d,
          columnMain: d?.columnMain
            ? d?.columnMain.charAt(0).toUpperCase() + d?.columnMain.slice(1)
            : '',
          averageAge: '',
        }
      }
      return {
        ...d,
        columnMain: d?.columnMain
          ? d?.columnMain.charAt(0).toUpperCase() + d?.columnMain.slice(1)
          : '',
      }
    })

    createExcelReportSourceSummary(
      JSON.stringify({ data: dataDownload, uniqueCourse, summary })
    )
      .then(async (i) => {
        const url = i.data.fileUrl
        const fileName = i.data.fileName
        await axios({
          url,
          method: 'GET',
          responseType: 'blob',
        })
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${fileName}`)
            document.body.appendChild(link)
            link.click()
          })
          .catch((err) => {
            console.error(err)
            setDownloadIsLoading(false)
          })
        setDownloadIsLoading(false)
      })
      .finally(() => {
        setDownloadIsLoading(false)
      })
  }
  useEffect(() => {
    setSearch({
      ...searchQuery,
      month:
        searchQuery.summary === 'annually'
          ? moment().format('YYYY')
          : moment().format('YYYY-MM'),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery.summary])

  useEffect(() => {
    if (reportSourceSummary) {
      setDataQuery(reportSourceSummary)
    }
  }, [reportSourceSummary])

  return (
    <Box>
      <Text color='brand'>Search</Text>
      <Box
        borderWidth='1px'
        borderColor='brand'
        borderRadius='10px'
        p={5}
        mb={5}
        mt={1}
      >
        <HStack
          spacing={{ base: 0, md: '20px' }}
          flexDirection={{ base: 'column', sm: 'column', md: 'row' }}
        >
          <FormControl>
            <FormLabel>
              <Text>Summary</Text>
            </FormLabel>
            <Select
              id='sammary'
              onChange={(e) => {
                setSearch({
                  ...searchQuery,
                  summary: e.target.value,
                  branch:
                    e.target.value === 'monthly' ? 'all' : searchQuery.branch,
                })
              }}
              value={searchQuery.summary}
            >
              {[
                { value: 'annually', label: 'Annual' },
                { value: 'monthly', label: 'Monthly' },
              ].map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <HStack w='full' justifyContent='space-between'>
              <FormLabel>
                {searchQuery.summary === 'annually' ? 'Year' : 'Month'}
              </FormLabel>
            </HStack>
            {searchQuery.summary === 'annually' ? (
              <DatePicker
                allowClear={false}
                picker='year'
                format={'YYYY'}
                value={moment(searchQuery.month)}
                onChange={(date) =>
                  setSearch({
                    ...searchQuery,
                    month: moment(date).format('YYYY'),
                  })
                }
              />
            ) : (
              <DatePicker
                allowClear={false}
                picker='month'
                format={'MMMM YYYY'}
                value={moment(searchQuery.month)}
                onChange={(date) =>
                  setSearch({
                    ...searchQuery,
                    month: moment(date).format('YYYY-MM'),
                  })
                }
              />
            )}
          </FormControl>
          {searchQuery.summary === 'annually' && (
            <FormControl>
              <FormLabel>Branch</FormLabel>
              <Select
                id='branch'
                placeholder='Pleas Select branch'
                onChange={(e) =>
                  setSearch({ ...searchQuery, branch: e.target.value })
                }
                value={searchQuery.branch}
                isDisabled={managerBranchId}
              >
                {branches
                  .concat({
                    id: 'all',
                    name: 'All',
                    createdDate: new Date(),
                  })
                  .sort(
                    (a, b) =>
                      b.accountType.localeCompare(a.accountType) ||
                      new Date(b.createdDate) - new Date(a.createdDate)
                  )
                  .map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
              </Select>
            </FormControl>
          )}
          <HStack alignSelf={'end'}>
            <IconButton
              variant='outline'
              fontSize={'23px'}
              icon={<FaFileExport color='#8CC63E' />}
              onClick={() => {
                handleDownloadFile({
                  data: dataQuery.result,
                  uniqueCourse: dataQuery.uniqueCourse,
                  summary: searchQuery.summary,
                })
              }}
              disabled={
                downloadIsLoading ||
                (searchQuery.summary === 'annually'
                  ? searchQuery.branch === ''
                    ? true
                    : dataQuery.result.length === 0
                  : dataQuery.result.length === 0)
              }
              isLoading={downloadIsLoading}
            />
          </HStack>
        </HStack>
      </Box>
      {/* </form> */}
      {searchQuery.branch ? (
        <>
          <SourceSummaryTable
            overflowX={'scroll'}
            columnsData={columnsData}
            tableData={dataQuery.result || []}
            noSearch
            loading={isLoading}
            showAll
            rowProps={(row) => {
              return row?.original?.columnMain?.toLowerCase() === 'total' ||
                row?.original?.columnMain?.toLowerCase() === 'average'
                ? {
                    backgroundColor:
                      row?.original?.columnMain?.toLowerCase() === 'total'
                        ? '#f0f0f0'
                        : row?.original?.columnMain?.toLowerCase() === 'average'
                        ? '#e0e0e0'
                        : 'none',
                    fontWeight: 'bold',
                  }
                : {}
            }}
          />
        </>
      ) : (
        <Center>
          <Heading>Please Select Branch</Heading>
        </Center>
      )}
    </Box>
  )
}
